import { useMemo } from 'react';
import type { ProjectType } from '@atlassian/jira-common-constants';
import { ff } from '@atlassian/jira-feature-flagging';
import {
	LEARN_MORE_LINK,
	LEARN_MORE_LINK_JSW,
	TROUBLESHOOT_PAGE_URL,
	TROUBLESHOOT_PAGE_URL_SOFTWARE,
} from '../../constants';
import { ProjectTypes } from '../../constants/project';
import { type Source, EXISTING_PROJECT } from '../../constants/source';
import messages from './messages';

export const useProjectSpecificContent = (type: ProjectType | null, source?: Source) =>
	useMemo(() => {
		let messagesObject;
		switch (type) {
			case ProjectTypes.SOFTWARE:
				messagesObject = {
					uploadHeading: messages.uploadHeadingJswSpork,
					uploadDescription: messages.uploadDescriptionWithLinkJswSpork,
					projectCreateHeading: ff('jira-business-to-software-project-imports')
						? messages.projectCreateHeadingSoftwareProject
						: messages.projectCreateHeadingJsw,
					projectCreateDescription: ff('jira-business-to-software-project-imports')
						? messages.projectCreateDescriptionSoftwareProject
						: messages.projectCreateDescriptionJsw,
					uploadCsvDropzoneHeading: messages.uploadCsvDropzoneHeadingJsw,
					uploadCsvDropzoneLearnMore: messages.uploadCsvDropzoneLearnMoreJsw,
					uploadFooterContinue: messages.uploadFooterContinue,
					uploadFooterCancel: messages.uploadFooterCancel,
					mappingHeading: messages.mappingHeadingJsw,
					mappingDescription: messages.mappingDescriptionJswSpork,
					mappingDescriptionWithLink: messages.mappingDescriptionWithLinkJswSpork,
					mappingLearnMore: messages.mappingLearnMoreJsw,
					importFromOtherAppsHeading: messages.importFromOtherAppsHeadingJsw,
					importFromOtherAppsHelperMessage: messages.importFromOtherAppsHelperMessageJsw,
					projectCreateMoreOptions: messages.projectCreateMoreOptionsJsw,
					projectCreateHideMoreOptions: messages.projectCreateHideMoreOptionsJsw,
					mappingAdvancedOptionsHeading: messages.mappingAdvancedOptionsHeading,
					mappingAdvancedOptionsPopupHeading: messages.mappingAdvancedOptionsPopupHeading,
					mappingAdvancedOptionsPopupDescription:
						messages.mappingAdvancedOptionsPopupDescriptionJsw,
					importInProgressHeading: messages.importInProgressHeading,
					importInProgressDescription: messages.importInProgressDescription,
					importInProgressProjectStep: messages.importInProgressCreateProjectStep,
					importInProgressErrorHeading: messages.importInProgressErrorHeading,
					closeButtonLabel: messages.close,
					uploadLearnMoreLink: LEARN_MORE_LINK_JSW,
					importInProgressTroubleshootLink: TROUBLESHOOT_PAGE_URL_SOFTWARE,
				};
				break;
			case ProjectTypes.BUSINESS:
				messagesObject = {
					uploadHeading: messages.uploadHeadingJwmSpork,
					uploadDescription: messages.uploadDescriptionWithLinkJwmComplexspork,
					projectCreateHeading: messages.projectCreateHeadingJwm,
					projectCreateDescription: messages.projectCreateDescriptionJwmSpork,
					uploadCsvDropzoneHeading: messages.uploadCsvDropzoneHeadingJwm,
					uploadCsvDropzoneLearnMore: messages.uploadCsvDropzoneLearnMoreJwm,
					uploadFooterContinue: messages.uploadFooterContinue,
					uploadFooterCancel: messages.uploadFooterCancel,
					mappingHeading: messages.mappingHeadingJwm,
					mappingDescription: messages.mappingDescriptionJwmSpork,
					mappingDescriptionWithLink: messages.mappingDescriptionWithLinkJwmSpork,
					mappingLearnMore: messages.mappingLearnMoreJwm,
					importFromOtherAppsHeading: messages.importFromOtherAppsHeadingJwm,
					importFromOtherAppsHelperMessage: messages.importFromOtherAppsHelperMessageJwm,
					importFromOtherAppsLearnMoreOtherApps: messages.importFromOtherAppsLearnMoreOtherAppsJwm,
					projectCreateMoreOptions: messages.projectCreateMoreOptionsJwm,
					projectCreateHideMoreOptions: messages.projectCreateHideMoreOptionsJwm,
					mappingAdvancedOptionsHeading: messages.mappingAdvancedOptionsHeading,
					mappingAdvancedOptionsPopupHeading: messages.mappingAdvancedOptionsPopupHeading,
					mappingAdvancedOptionsPopupDescription:
						messages.mappingAdvancedOptionsPopupDescriptionJwm,
					importInProgressHeading: messages.importInProgressHeading,
					importInProgressDescription: messages.importInProgressDescription,
					importInProgressProjectStep: messages.importInProgressCreateProjectStep,
					importInProgressErrorHeading: messages.importInProgressErrorHeading,
					closeButtonLabel: messages.close,
					uploadLearnMoreLink: LEARN_MORE_LINK,
					importInProgressTroubleshootLink: TROUBLESHOOT_PAGE_URL,
				};
				break;
			default:
				messagesObject = {
					uploadHeading: messages.emptyMessageNoTranslate,
					uploadDescription: messages.emptyMessageNoTranslate,
					uploadLearnMore: messages.emptyMessageNoTranslate,
					uploadReturn: messages.emptyMessageNoTranslate,
					uploadFooterContinue: messages.emptyMessageNoTranslate,
					uploadFooterCancel: messages.emptyMessageNoTranslate,
					projectCreateHeading: messages.emptyMessageNoTranslate,
					projectCreateDescription: messages.emptyMessageNoTranslate,
					uploadCsvDropzoneHeading: messages.emptyMessageNoTranslate,
					uploadCsvDropzoneLearnMore: messages.emptyMessageNoTranslate,
					mappingHeading: messages.emptyMessageNoTranslate,
					mappingDescription: messages.emptyMessageNoTranslate,
					mappingDescriptionWithLink: messages.emptyMessageNoTranslate,
					mappingLearnMore: messages.emptyMessageNoTranslate,
					importFromOtherAppsHeading: messages.emptyMessageNoTranslate,
					importFromOtherAppsHelperMessage: messages.emptyMessageNoTranslate,
					importFromOtherAppsLearnMoreOtherApps: messages.emptyMessageNoTranslate,
					projectCreateMoreOptions: messages.emptyMessageNoTranslate,
					projectCreateHideMoreOptions: messages.emptyMessageNoTranslate,
					mappingAdvancedOptionsHeading: messages.emptyMessageNoTranslate,
					mappingAdvancedOptionsPopupHeading: messages.emptyMessageNoTranslate,
					mappingAdvancedOptionsPopupDescription: messages.emptyMessageNoTranslate,
					importInProgressHeading: messages.emptyMessageNoTranslate,
					importInProgressDescription: messages.emptyMessageNoTranslate,
					importInProgressProjectStep: messages.emptyMessageNoTranslate,
					importInProgressErrorHeading: messages.emptyMessageNoTranslate,
					closeButtonLabel: messages.emptyMessageNoTranslate,
					uploadLearnMoreLink: LEARN_MORE_LINK,
					importInProgressTroubleshootLink: TROUBLESHOOT_PAGE_URL,
				};
		}
		if (source === EXISTING_PROJECT) {
			messagesObject = {
				...messagesObject,
				mappingHeading: messages.mappingHeadingJsw,
				mappingDescription: messages.mappingDescriptionExistingProjectNonFinal,
				mappingDescriptionWithLink: messages.mappingDescriptionWithLinkExistingProject,
				mappingLearnMore: messages.mappingLearnMoreExistingProjectNonFinal,
				importInProgressHeading: messages.importInProgressHeadingExistingProject,
				importInProgressDescription: messages.importInProgressDescriptionExistingProject,
				importInProgressProjectStep: messages.importInProgressMappingDataStep,
				importInProgressErrorHeading: messages.importInProgressErrorExistingProjectHeading,
			};
		}
		return messagesObject;
	}, [source, type]);
